import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import styled from '@emotion/styled';
import { IconKey } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, isCompanyAdmin, isManufacturerAdmin, ROLE_LABELS, getEffectiveRole } from '../services/users';
import ChangePasswordModal from './ChangePasswordModal';

// Single source of truth for the fixed sidebar width — used by both the
// Sidebar and the MainContent left margin so they can't drift apart.
const SIDEBAR_WIDTH = '240px';

const LayoutWrapper = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Sidebar = styled.aside`
  width: ${SIDEBAR_WIDTH};
  background-color: ${({ theme }) => theme.background.secondary};
  color: ${({ theme }) => theme.font.color.secondary};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 20;
  /* Sidebar and page now share canvas-soft; a hairline keeps them distinct. */
  border-right: 1px solid ${({ theme }) => theme.border.color.medium};
`;

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing(6)};
  border-bottom: 1px solid ${({ theme }) => theme.border.color.light};

  h1 {
    font-size: ${({ theme }) => theme.font.size.xl};
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0;
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: ${({ theme }) => theme.spacing(4, 0)};
  overflow-y: auto;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(3, 6)};
  color: ${({ theme }) => theme.font.color.secondary};
  text-decoration: none;
  transition: ${({ theme }) => theme.animation.transition.standard};
  font-size: ${({ theme }) => theme.font.size.md};

  &:hover {
    background-color: ${({ theme }) => theme.background.tertiary};
    color: ${({ theme }) => theme.font.color.primary};
  }

  &.active {
    background-color: ${({ theme }) => theme.background.tertiary};
    color: ${({ theme }) => theme.font.color.primary};
    border-left: 3px solid ${({ theme }) => theme.accent.primary};
  }
`;

const SidebarFooter = styled.div`
  padding: ${({ theme }) => theme.spacing(4, 6)};
  border-top: 1px solid ${({ theme }) => theme.border.color.light};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const UserRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  overflow: hidden;
`;

const UserName = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.primary};
  font-size: ${({ theme }) => theme.font.size.md};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.tertiary};
`;

const UserCompany = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.light};
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterIcons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

// Quiet secondary controls — align to the spec's Secondary button tokens
// (hairline-strong border, sm radius) rather than ad-hoc rem/px values.
const IconButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.border.color.strong};
  color: ${({ theme }) => theme.font.color.secondary};
  padding: ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.animation.transition.standard};

  &:hover {
    background-color: ${({ theme }) => theme.background.tertiary};
    color: ${({ theme }) => theme.font.color.primary};
  }
`;

const LogoutBtn = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.border.color.strong};
  color: ${({ theme }) => theme.font.color.secondary};
  padding: ${({ theme }) => theme.spacing(1, 3)};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  transition: ${({ theme }) => theme.animation.transition.standard};

  &:hover {
    background-color: ${({ theme }) => theme.background.tertiary};
    color: ${({ theme }) => theme.font.color.primary};
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${SIDEBAR_WIDTH};
  padding: ${({ theme }) => theme.spacing(8)};
  overflow-y: auto;
  /* Spec: page background is canvas-soft so flat white cards lift without a
     shadow. Cards themselves stay on background.primary (canvas). */
  background-color: ${({ theme }) => theme.background.secondary};
  min-height: 100vh;
`;

function Layout() {
  const { user, logout } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);

  const userIsSuperAdmin = isSuperAdmin(user);
  const userIsCompanyAdmin = isCompanyAdmin(user);
  const userIsManufacturerAdmin = isManufacturerAdmin(user);
  const effectiveRole = getEffectiveRole(user);

  return (
    <LayoutWrapper>
      <Sidebar>
        <SidebarHeader>
          <h1>Simkura</h1>
        </SidebarHeader>
        <SidebarNav>
          {userIsManufacturerAdmin ? (
            /* Marketplace seller: their provider page + their fleet. */
            <>
              <NavItem to={`/hardware-providers/${user?.manufacturer_id}`}>{user?.manufacturer_name || 'My hardware'}</NavItem>
              <NavItem to="/hardware-devices">Devices</NavItem>
              <NavItem to="/hardware-orders">Orders</NavItem>
            </>
          ) : (
            <>
              <NavItem to="/dashboard">Dashboard</NavItem>
              <NavItem to="/devices">Devices</NavItem>
              {/* Marketplace: catalog + cart, then order history / recurring
                  cost / payment methods (Structure.md navigation). */}
              {userIsCompanyAdmin && !userIsSuperAdmin && (
                <>
                  <NavItem to="/store">Purchase Material</NavItem>
                  <NavItem to="/purchases">Purchases</NavItem>
                </>
              )}

              {/* Admin-only since the per-user Connected Apps surface was
                  retired — the hub is all company-scoped management now. */}
              {userIsCompanyAdmin && (
                <NavItem to="/integrations">Integrations</NavItem>
              )}

              {/* Company admins manage their org (billing, payment, users)
                  from System. Super admins keep the platform-wide Users page
                  — System is pinned to the signed-in user's own company. */}
              {userIsCompanyAdmin && !userIsSuperAdmin && (
                <NavItem to="/system">System</NavItem>
              )}

              {userIsSuperAdmin && (
                <NavItem to="/users">Users</NavItem>
              )}

              {userIsSuperAdmin && (
                <NavItem to="/billing">Billing</NavItem>
              )}

              {userIsSuperAdmin && (
                <NavItem to="/software-providers">Software Providers</NavItem>
              )}

              {userIsSuperAdmin && (
                <NavItem to="/hardware-providers">Hardware Providers</NavItem>
              )}

              {userIsSuperAdmin && (
                <NavItem to="/admin">Admin</NavItem>
              )}
            </>
          )}
        </SidebarNav>
        <SidebarFooter>
          <UserRow>
            <UserInfo>
              <UserName>{user?.name}</UserName>
              <UserRole>{ROLE_LABELS[effectiveRole] || effectiveRole}</UserRole>
              {(user?.company_name || user?.manufacturer_name) && (
                <UserCompany>{user.company_name || user.manufacturer_name}</UserCompany>
              )}
            </UserInfo>
          </UserRow>
          <FooterActions>
            <FooterIcons>
              <IconButton onClick={() => setShowChangePassword(true)} title="Change password">
                <IconKey size={16} stroke={1.5} />
              </IconButton>
            </FooterIcons>
            <LogoutBtn onClick={logout}>Logout</LogoutBtn>
          </FooterActions>
        </SidebarFooter>
      </Sidebar>
      <MainContent>
        <Outlet />
      </MainContent>
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </LayoutWrapper>
  );
}

export default Layout;
